import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import { AuditLogger } from '../../../utils/auditLogger';
import { fetchCostsData, saveCost, deleteCost } from '../services/billService';
import useBillsStore from '../store/billsStore';

function capitalizeFirstLetter(str) {
  if (!str) return str;
  return str.trim().split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
}

export function useCosts() {
  const { user } = useAuth();
  const { t } = useTranslation('home');

  const [costs, setCosts] = useState([]);

  const loadCosts = useCallback(async () => {
    if (!user?.businessId) return;
    try {
      const data = await fetchCostsData({ businessId: user.businessId, role: user.role });
      setCosts(data);
    } catch (err) {
      console.error('Giderler getirilirken hata:', err);
    }
  }, [user?.businessId, user?.role]);

  const groupCostsByDate = useCallback((costsArr) =>
    costsArr.reduce((groups, cost) => {
      const date = cost.date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(cost);
      return groups;
    }, {}),
  []);

  const handleDeleteCost = useCallback(async (costId) => {
    try {
      await deleteCost(costId);
      await AuditLogger.log(user.uid, 'delete', 'cost', costId, { status: 'deleted' });
      toast.success(t('toasts.costDeleted'));
      await loadCosts();
    } catch (err) {
      console.error('Silme hatası:', err);
      toast.error(t('toasts.deleteError'));
    }
  }, [user, t, loadCosts]);

  const handleSaveCost = useCallback(async () => {
    const { costForm, selectedCostId, setIsCostSaving, closeCostModal } =
      useBillsStore.getState();

    if (!costForm.price) {
      toast.error(t('toasts.costAmountRequired'));
      return;
    }

    setIsCostSaving(true);
    try {
      const costData = {
        ...costForm,
        description: costForm.description
          ? capitalizeFirstLetter(costForm.description)
          : '',
        price: Number(costForm.price),
        status: 'active',
        userId: user.uid,
        businessId: user.businessId,
      };

      await saveCost({ costData, costId: selectedCostId });
      await loadCosts();
      closeCostModal();
      toast.success(selectedCostId ? t('toasts.costUpdated') : t('toasts.costAdded'));
    } catch (err) {
      console.error('İşlem hatası:', err);
      toast.error(t('toasts.saveError', { error: err.message }));
    } finally {
      useBillsStore.getState().setIsCostSaving(false);
    }
  }, [user, t, loadCosts]);

  return {
    costs,
    loadCosts,
    groupCostsByDate,
    handleDeleteCost,
    handleSaveCost,
  };
}
